<template>
  <div v-if="dataType === 'date'">
    <b-input type="date" v-model="editorData" @input="updateDataState"></b-input>
  </div>
  <div id="WysiwygTextEditor" v-else>
    <ckeditor
      ref="editor"
      :editor="editor"
      v-model="editorData"
      :config="editorConfig"
      @input="updateDataState"
    ></ckeditor>
  </div>
</template>

<script>
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'

import EssentialsPlugin from '@ckeditor/ckeditor5-essentials/src/essentials'
import AlignmentPlugin from '@ckeditor/ckeditor5-alignment/src/alignment'
import BoldPlugin from '@ckeditor/ckeditor5-basic-styles/src/bold'
import ItalicPlugin from '@ckeditor/ckeditor5-basic-styles/src/italic'
import LinkPlugin from '@ckeditor/ckeditor5-link/src/link'
import ParagraphPlugin from '@ckeditor/ckeditor5-paragraph/src/paragraph'
import Heading from '@ckeditor/ckeditor5-heading/src/heading'
import List from '@ckeditor/ckeditor5-list/src/list'
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote'
import Table from '@ckeditor/ckeditor5-table/src/table'
import Image from '@ckeditor/ckeditor5-image/src/image'
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar'
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption'
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle'
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize'
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload'
import Subscript from '@ckeditor/ckeditor5-basic-styles/src/subscript'
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript'
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed'
import AutoMediaEmbed from '@ckeditor/ckeditor5-media-embed/src/automediaembed'

import { mapMutations, mapGetters, mapActions } from 'vuex'

import MyUploadAdapter from '@/components/content/edit/UploadAdapter'

import _ from 'lodash'

function MyCustomUploadAdapterPlugin(editor) {
  // eslint-disable-next-line no-param-reassign
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => new MyUploadAdapter(loader)
}

export default {
  name: 'WysiwygTextEditor',
  props: {
    dataType: String, // title, shortDescription, fullDescription, date, courseShortDescription
    emptyToolbar: Boolean,
    shortDescription: Boolean,
    currentVariant: Number,
    clearUpdaterOnDestroy: {
      type: Boolean,
      default: false
    },
    dataForView: {
      type: String,
      default() {
        return null
      }
    }
  },
  data() {
    return {
      isFirtsTime: true,
      editor: ClassicEditor,
      editorConfig: {
        // The configuration of the editor.
        plugins: [
          AlignmentPlugin,
          EssentialsPlugin,
          BoldPlugin,
          ItalicPlugin,
          LinkPlugin,
          ParagraphPlugin,
          Heading,
          List,
          BlockQuote,
          Table,
          Image,
          ImageToolbar,
          ImageCaption,
          ImageStyle,
          ImageResize,
          ImageUpload,
          Subscript,
          Superscript,
          MediaEmbed,
          AutoMediaEmbed
        ],
        extraPlugins: [MyCustomUploadAdapterPlugin],
        language: 'ru',
        toolbar: [],
        heading: {
          options: [
            { model: 'paragraph', title: 'Параграф', class: 'ck-heading_paragraph' },
            {
              model: 'heading1', view: 'h1', title: 'Заголовок 1', class: 'ck-heading_heading1'
            },
            {
              model: 'heading2', view: 'h2', title: 'Заголовок 2', class: 'ck-heading_heading2'
            },
            {
              model: 'heading3', view: 'h3', title: 'Заголовок 3', class: 'ck-heading_heading3'
            },
            {
              model: 'heading4', view: 'h4', title: 'Заголовок 4', class: 'ck-heading_heading4'
            },
            {
              model: 'heading5', view: 'h5', title: 'Заголовок 5', class: 'ck-heading_heading5'
            }
          ]
        },
        image: {
          toolbar: [
            'imageTextAlternative',
            '|',
            'imageStyle:alignLeft',
            'imageStyle:alignCenter',
            'imageStyle:alignRight'
          ],
          styles: [
            'alignCenter',
            'alignLeft',
            'alignRight'
          ],
          resizeUnit: 'px'
        },
        mediaEmbed: {
          previewsInData: true,
          removeProviders: ['instagram', 'twitter', 'googleMaps', 'flickr',
            'facebook', 'dailymotion', 'spotify', 'vimeo']
        }
      }
    }
  },
  computed: {
    editorData: {
      get() {
        if (_.isString(this.dataForView)) {
          return this.dataForView
        }
        if (this.dataType === 'courseShortDescription') {
          return this.course.contentObject.shortDescription
        }
        if (this.dataType === 'subSectionText') {
          return this.subsection.text
        }
        if ((this.dataType === 'subSectionTextTestVariants') && this.variants) {
          return (this.variants[this.currentVariant]) ? this.variants[this.currentVariant] : ''
        }
        if (this.content.contentObject) {
          if (this.dataType === 'title') {
            return this.content.contentObject.title
          }
          if (this.dataType === 'shortDescription') {
            return this.content.contentObject.shortDescription
          }
          if (this.dataType === 'fullDescription') {
            return this.content.contentObject.fullDescription
          }
          if (this.dataType === 'date') {
            if (this.content.contentObject.scheduleDate) {
              return this.content.contentObject.scheduleDate.substring(0, 10)
            }
            return this.content.contentObject.scheduleDate
          }
        }

        return null
      },
      set(newValue) {
        if (_.isString(this.dataForView)) {
          this.$emit('update', newValue)
        } else {
          switch (this.dataType) { // eslint-disable-line default-case
            case 'title':
              this.updateTitle(newValue)
              break
            case 'shortDescription':
              this.updateShortDescription(newValue)
              break
            case 'fullDescription':
              this.updateFullDescription(newValue)
              break
            case 'date':
              this.updateDate(newValue)
              break
            case 'courseShortDescription':
              this.updateCourseShortDescription(newValue)
              break
            case 'subSectionText':
              this.setSubsectionText(newValue)
              break
            case 'subSectionTextTestVariants':
              this.setSubsectionTextTestVariant({key: this.currentVariant, text: newValue})
              break
          }
        }
      }
    },
    ...mapGetters({
      course: 'education/getCourseContent',
      content: 'content/getContent',
      isContentSaved: 'contentManager/isContentSaved',
      updater: 'contentManager/getUpdater',
      subsection: 'education/getSubsection',
      variants: 'education/getVariantsForTest'
    })
  },
  created() {
    if (this.emptyToolbar) {
      this.editorConfig.toolbar = []
    } else if (this.shortDescription || this.dataType === 'courseShortDescription') {
      this.editorConfig.toolbar = ['undo', 'redo', '|', 'heading',
        'bold', 'italic', 'bulletedList', 'numberedList', 'blockQuote', 'subscript', 'superscript',
        'link']
    } else {
      this.editorConfig.toolbar = ['undo', 'redo', '|', 'heading',
        'bold', 'italic', 'alignment', 'bulletedList', 'numberedList', 'blockQuote', 'subscript', 'superscript',
        'insertTable', 'link', 'imageUpload', 'mediaEmbed']
    }
  },
  updated() {
    if (this.updater > 0) {
      this.updateContentSavedStatus(false)
    }
  },
  beforeDestroy() {
    if (this.clearUpdaterOnDestroy) {
      this.updateUpdater(0)
    }
    this.isFirtsTime = true
  },
  methods: {
    updateDataState() {
      this.updateUpdater(this.updater + 1)
    },
    updateCourseShortDescription(shortDescription) {
      this.updateCourseContent({
        shortDescription
      })
    },
    ...mapMutations({
      updateContentSavedStatus: 'contentManager/updateContentSavedStatus',
      updateCourseContent: 'education/updateCourseContent',
      setSubsectionText: 'education/setSubsectionText',
      setSubsectionTextTestVariant: 'education/setSubsectionTextTestVariant'
    }),
    ...mapActions({
      updateTitle: 'contentManager/updateTitle',
      updateShortDescription: 'contentManager/updateShortDescription',
      updateFullDescription: 'contentManager/updateFullDescription',
      updateDate: 'contentManager/updateDate',
      updateUpdater: 'contentManager/updateUpdater'
    })
  }
}
</script>

<style lang="scss">
@media screen and (min-width: 992px) {
  .ck .ck-sticky-panel .ck-sticky-panel__content_sticky{
    margin-top: 80px;
    margin-bottom: 80px;
  }
}
</style>
